import { jsonLdScriptProps } from 'react-schemaorg';
import { JobPosting, WithContext } from 'schema-dts';

import Head from 'next/head';

import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import { TypeArea, TypeCity, TypeJobAdvert } from 'generated/contentful';

interface JobAdvertSchemaProps {
  jobAdvert: TypeJobAdvert<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  area: TypeArea<'WITHOUT_UNRESOLVABLE_LINKS', string> | null;
  city: TypeCity<'WITHOUT_UNRESOLVABLE_LINKS', string>;
  publishedDate: string;
  closingDate: string;
}

const JobAdvertSchema = ({ jobAdvert, area, city, publishedDate, closingDate }: JobAdvertSchemaProps) => {
  const structuredData: WithContext<JobPosting> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    // TODO: HK-4343 confirm weekly pay figure (currently £13.50 base × 50hrs = £675)
    title: `${jobAdvert.fields.title} | Up to £675/Week + Tips | ${jobAdvert.fields.employmentType.join(
      ' or '
    )} | Immediate Start | Local Cleaning Job`,
    description: jobAdvert.fields.description ? documentToHtmlString(jobAdvert.fields.description) : '',
    identifier: {
      '@type': 'PropertyValue',
      name: 'Housekeep',
      value: publishedDate + '-' + jobAdvert.fields.slug
    },
    datePosted: publishedDate,
    validThrough: closingDate,
    employmentType: jobAdvert.fields.employmentType.map(type => type.toUpperCase().replace('-', '_')),
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Housekeep',
      sameAs: 'https://housekeep.com',
      logo: 'https://housekeep.com/next-assets/design-tokens/app/icon/icon.png'
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '',
        addressLocality: area ? area.fields.name : city.fields.name,
        addressRegion: city.fields.name,
        addressCountry: 'GB'
      }
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'GBP',
      value:
        jobAdvert.fields.minimumPay === jobAdvert.fields.maximumPay
          ? {
              '@type': 'QuantitativeValue',
              value: jobAdvert.fields.minimumPay,
              unitText: jobAdvert.fields.payFrequency
            }
          : {
              '@type': 'QuantitativeValue',
              minValue: jobAdvert.fields.minimumPay,
              maxValue: jobAdvert.fields.maximumPay,
              unitText: jobAdvert.fields.payFrequency
            }
    },
    directApply: true,
    jobImmediateStart: true,
    incentiveCompensation: 'Bonus Pay, Tips'
  };

  return (
    <Head>
      <script {...jsonLdScriptProps<JobPosting>(structuredData)} />
    </Head>
  );
};

export default JobAdvertSchema;
